import {
    useReactTable,
    getCoreRowModel,
    flexRender,
} from '@tanstack/react-table';
import React, { useState, useEffect, useRef } from 'react';
import { createPortal, } from 'react-dom';
import { useNavigate, useParams } from "react-router-dom";

import {
    FiUser, FiHash, FiCalendar, FiClock, FiTool, FiDollarSign,
    FiHome, FiLayers, FiCheckCircle, FiAlertCircle,
    FiX, FiGrid, FiList, FiChevronRight
} from "react-icons/fi";
import { GiWoodBeam } from "react-icons/gi";
import { BrickWall, Grid2X2 } from "lucide-react";

import AssignmentContextMenu from './AssignmentContextMenu';
import { handleProductionAction, updateProductionPhase } from '../../../lib/supabase/production';


const StickyToolbar = ({
    searchQuery,
    setSearchQuery,
    selectedQuoteTypes,
    setSelectedQuoteTypes,
    viewMode,
    setViewMode,
    columnVisibility,
    setColumnVisibility,
    isVisible,
    navbarHeight,
    expandedAssignments,
    filteredAssignments
}) => {
    const [showColumnDropdown, setShowColumnDropdown] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowColumnDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div
            className={`fixed left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-lg transition-all duration-300 ${isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
            style={{
                top: `${navbarHeight}px`,
                transform: isVisible ? 'translateY(0)' : `translateY(-100%)`
            }}
        >
            <div className="max-w-[1800px] mx-auto px-4 py-3">
                <div className="flex items-center gap-3">
                    {/* Compact Search */}
                    <div className="relative flex-1 min-w-[250px] max-w-[400px]">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search..."
                            className="w-full h-8 px-3 pl-8 pr-8 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <FiGrid className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <FiX className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>

                    {/* Quote Type Filters */}
                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={() => setSelectedQuoteTypes(prev => ({ ...prev, roof: !prev.roof }))}
                            className={`flex items-center gap-1 h-8 px-2 rounded text-xs font-medium transition-all ${selectedQuoteTypes.roof
                                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                : 'bg-gray-100 text-gray-500 border border-gray-200'
                                }`}
                            title="Roof"
                        >
                            <FiHome className="w-3 h-3" />
                            <span className="hidden sm:inline">Roof</span>
                        </button>
                        <button
                            onClick={() => setSelectedQuoteTypes(prev => ({ ...prev, wall: !prev.wall }))}
                            className={`flex items-center gap-1 h-8 px-2 rounded text-xs font-medium transition-all ${selectedQuoteTypes.wall
                                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                : 'bg-gray-100 text-gray-500 border border-gray-200'
                                }`}
                            title="Wall"
                        >
                            <BrickWall className="w-3 h-3" />
                            <span className="hidden sm:inline">Wall</span>
                        </button>
                        <button
                            onClick={() => setSelectedQuoteTypes(prev => ({ ...prev, floor: !prev.floor }))}
                            className={`flex items-center gap-1 h-8 px-2 rounded text-xs font-medium transition-all ${selectedQuoteTypes.floor
                                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                : 'bg-gray-100 text-gray-500 border border-gray-200'
                                }`}
                            title="Floor"
                        >
                            <Grid2X2 className="w-3 h-3" />
                            <span className="hidden sm:inline">Floor</span>
                        </button>
                        <button
                            onClick={() => setSelectedQuoteTypes(prev => ({ ...prev, timber: !prev.timber }))}
                            className={`flex items-center gap-1 h-8 px-2 rounded text-xs font-medium transition-all ${selectedQuoteTypes.timber
                                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                : 'bg-gray-100 text-gray-500 border border-gray-200'
                                }`}
                            title="Timber"
                        >
                            <GiWoodBeam className="w-3 h-3" />
                            <span className="hidden sm:inline">Timber</span>
                        </button>
                    </div>

                    {/* View Mode Toggle */}
                    <div className="flex bg-gray-100 rounded-md border h-8">
                        <button
                            onClick={() => setViewMode('separated')}
                            className={`px-2 h-full text-xs font-medium rounded transition-colors ${viewMode === 'separated'
                                ? 'bg-white text-blue-600 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                                }`}
                        >
                            Separated
                        </button>
                        <button
                            onClick={() => setViewMode('combined')}
                            className={`px-2 h-full text-xs font-medium rounded transition-colors ${viewMode === 'combined'
                                ? 'bg-white text-blue-600 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                                }`}
                        >
                            Combined
                        </button>
                    </div>

                    {/* Column Visibility Dropdown */}
                    <div className="relative" ref={dropdownRef}>
                        <button
                            onClick={() => setShowColumnDropdown(!showColumnDropdown)}
                            className="flex items-center gap-1.5 h-8 px-3 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                            <FiList className="w-3.5 h-3.5" />
                            Columns
                            <FiChevronRight className={`w-3 h-3 transition-transform ${showColumnDropdown ? 'rotate-90' : ''}`} />
                        </button>

                        {showColumnDropdown && (
                            <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-[60] max-h-96 overflow-y-auto">
                                <div className="p-3 border-b border-gray-200 bg-gray-50 sticky top-0">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-semibold text-gray-700">Toggle Columns</span>
                                        <button
                                            onClick={() => {
                                                const allVisible = Object.values(columnVisibility).every(Boolean);
                                                const newVisibility = Object.keys(columnVisibility).reduce((acc, key) => {
                                                    acc[key] = !allVisible;
                                                    return acc;
                                                }, {});
                                                setColumnVisibility(newVisibility);
                                            }}
                                            className="text-xs font-medium text-blue-600 hover:text-blue-800"
                                        >
                                            {Object.values(columnVisibility).every(Boolean) ? 'Hide All' : 'Show All'}
                                        </button>
                                    </div>
                                </div>

                                <div className="p-2 space-y-1">
                                    {[
                                        { key: 'uniqueId', label: 'Job ID', icon: FiHash },
                                        { key: 'customerName', label: 'Customer', icon: FiUser },
                                        { key: 'jobType', label: 'Type', icon: FiTool },
                                        { key: 'mainStatus', label: 'Status', icon: FiClock },
                                        { key: 'quotes', label: 'Quotes', icon: FiLayers },
                                        { key: 'assignee', label: 'Assignee', icon: FiUser },
                                        { key: 'priority', label: 'Priority', icon: FiAlertCircle },
                                        { key: 'invoiced', label: 'Invoice', icon: FiDollarSign },
                                        { key: 'createdAt', label: 'Created', icon: FiCalendar },
                                        { key: 'etaDate', label: 'ETA Date', icon: FiCalendar },
                                    ].map(({ key, label, icon: Icon }) => (
                                        <label
                                            key={key}
                                            className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-gray-50 ${columnVisibility[key] ? 'bg-blue-50/50' : ''
                                                }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={columnVisibility[key] ?? true}
                                                onChange={(e) =>
                                                    setColumnVisibility(prev => ({
                                                        ...prev,
                                                        [key]: e.target.checked,
                                                    }))
                                                }
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                            <Icon className="w-3 h-3 text-gray-500" />
                                            <span className="text-xs text-gray-700">{label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Row Count - Right aligned */}
                    <div className="ml-auto">
                        <span className="bg-orange-100 text-orange-800 text-xs font-medium px-2.5 h-8 rounded-full inline-flex items-center">
                            {expandedAssignments.length} rows ({filteredAssignments.length} assignments)
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ColumnToggle = ({
    searchQuery,
    setSearchQuery,
    columnVisibility,
    setColumnVisibility,
    setColumnSizing,
    selectedQuoteTypes,
    setSelectedQuoteTypes
}) => (
    <div className="mb-4 space-y-3">
        {/* Search Bar */}
        <div className="relative">
            <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by Job ID, Customer, Assignee, Status..."
                className="w-full px-4 py-2.5 pl-10 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
            <FiGrid className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            {searchQuery && (
                <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                    <FiX className="w-4 h-4" />
                </button>
            )}
        </div>

        {/* Column Toggle Section */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <FiList className="w-4 h-4 text-gray-600" />
                        <span className="text-sm font-semibold text-gray-700">Column Visibility</span>
                    </div>
                    <div className='flex flex-row gap-3'>
                        <button
                            onClick={() => {
                                const allVisible = Object.values(columnVisibility).every(Boolean);
                                const newVisibility = Object.keys(columnVisibility).reduce((acc, key) => {
                                    acc[key] = !allVisible;
                                    return acc;
                                }, {});
                                setColumnVisibility(newVisibility);
                            }}
                            className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                        >
                            {Object.values(columnVisibility).every(Boolean) ? 'Hide All' : 'Show All'}
                        </button>
                        <button
                            onClick={() => {
                                setColumnVisibility({
                                    uniqueId: true,
                                    customerName: true,
                                    jobType: true,
                                    mainStatus: true,
                                    quotes: true,
                                    assignee: true,
                                    priority: true,
                                    createdAt: true,
                                    invoiced: true,
                                    etaDate: true
                                });
                                setColumnSizing({});
                            }}
                            className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                        >
                            Reset Table
                        </button>
                    </div>
                </div>
            </div>

            <div className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {[
                        { key: 'uniqueId', label: 'Job ID', icon: FiHash },
                        { key: 'customerName', label: 'Customer', icon: FiUser },
                        { key: 'jobType', label: 'Type', icon: FiTool },
                        { key: 'mainStatus', label: 'Status', icon: FiClock },
                        { key: 'quotes', label: 'Quotes', icon: FiLayers },
                        { key: 'assignee', label: 'Assignee', icon: FiUser },
                        { key: 'priority', label: 'Priority', icon: FiAlertCircle },
                        { key: 'invoiced', label: 'Invoice', icon: FiDollarSign },
                        { key: 'createdAt', label: 'Created', icon: FiCalendar },
                        { key: 'etaDate', label: 'ETA Date', icon: FiCalendar },
                    ].map(({ key, label, icon: Icon }) => (
                        <label
                            key={key}
                            className={`flex items-center gap-2 px-3 py-2 rounded-md border transition-all cursor-pointer ${columnVisibility[key]
                                ? 'bg-blue-50 border-blue-200 hover:bg-blue-100'
                                : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                                }`}
                        >
                            <input
                                type="checkbox"
                                checked={columnVisibility[key] ?? true}
                                onChange={(e) =>
                                    setColumnVisibility(prev => ({
                                        ...prev,
                                        [key]: e.target.checked,
                                    }))
                                }
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                            />
                            <Icon className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                            <span className="text-sm text-gray-700 font-medium">{label}</span>
                        </label>
                    ))}
                </div>
            </div>

            {/* Quote Type Filters */}
            <div className="flex items-start gap-2 flex-col p-4 pt-0">
                <span className="text-xs font-medium text-gray-600">Filter by Quote Type:</span>
                <div className='flex flex-row gap-2'>
                    <button
                        onClick={() => setSelectedQuoteTypes(prev => ({ ...prev, roof: !prev.roof }))}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${selectedQuoteTypes.roof
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : 'bg-gray-100 text-gray-500 border border-gray-200'
                            }`}
                    >
                        <FiHome className="w-3 h-3" />
                        Roof
                    </button>
                    <button
                        onClick={() => setSelectedQuoteTypes(prev => ({ ...prev, wall: !prev.wall }))}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${selectedQuoteTypes.wall
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : 'bg-gray-100 text-gray-500 border border-gray-200'
                            }`}
                    >
                        <BrickWall className="w-3 h-3" />
                        Wall
                    </button>
                    <button
                        onClick={() => setSelectedQuoteTypes(prev => ({ ...prev, floor: !prev.floor }))}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${selectedQuoteTypes.floor
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : 'bg-gray-100 text-gray-500 border border-gray-200'
                            }`}
                    >
                        <Grid2X2 className="w-3 h-3" />
                        Floor
                    </button>
                    <button
                        onClick={() => setSelectedQuoteTypes(prev => ({ ...prev, timber: !prev.timber }))}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${selectedQuoteTypes.timber
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : 'bg-gray-100 text-gray-500 border border-gray-200'
                            }`}
                    >
                        <GiWoodBeam className="w-3 h-3" />
                        Timber
                    </button>
                    <button
                        onClick={() => setSelectedQuoteTypes({ roof: true, wall: true, floor: true, timber: true })}
                        className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors ml-2"
                    >
                        Show All
                    </button>
                </div>
            </div>
        </div>
    </div>
);

const ProductionReadyTable = ({
    assignments,
    userType,
    company,
    navigate,
    availableDesigners,
    onLoadMore,
    hasMore,
    isLoadingMore,
    isLoading = false,
    scheduleType,
    showOnlyMine,
    selectedUser
}) => {
    const [viewMode, setViewMode] = useState('separated');
    const [expandedAssignments, setExpandedAssignments] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isTableLoading, setIsTableLoading] = useState(false);
    const [filteredAssignments, setFilteredAssignments] = useState(assignments);
    const [showStickyToolbar, setShowStickyToolbar] = useState(false);
    const [navbarHeight, setNavbarHeight] = useState(0);
    const columnToggleRef = useRef(null);

    const { companySlug } = useParams();

    const [selectedQuoteTypes, setSelectedQuoteTypes] = useState({
        roof: true,
        wall: true,
        floor: true,
        timber: true
    });

    const useDebounce = (value, delay) => {
        const [debouncedValue, setDebouncedValue] = useState(value);

        useEffect(() => {
            const handler = setTimeout(() => {
                setDebouncedValue(value);
            }, delay);

            return () => clearTimeout(handler);
        }, [value, delay]);

        return debouncedValue;
    };

    const debouncedSearchQuery = useDebounce(searchQuery, 300);

    const expandAssignmentsByQuoteType = (assignments) => {
        const expanded = [];

        assignments.forEach(assignment => {
            if (assignment.jobType === 'Estimation') {
                return;
            }

            if (viewMode === 'combined') {
                expanded.push({
                    ...assignment,
                    _displayId: assignment._id,
                    _originalId: assignment._id,
                    _quoteType: null,
                    _isCombined: true
                });
            } else {
                const completedQuotes = [];

                if (assignment.quotes?.roof?.isRequired &&
                    assignment.quotes?.roof?.designStatus === 'completed' &&
                    selectedQuoteTypes.roof) {
                    completedQuotes.push({
                        ...assignment,
                        _displayId: `${assignment._id}_roof`,
                        _originalId: assignment._id,
                        _quoteType: 'roof',
                        _quoteData: assignment.quotes.roof,
                        _isCombined: false
                    });
                }

                if (assignment.quotes?.wall?.isRequired &&
                    assignment.quotes?.wall?.designStatus === 'completed' &&
                    selectedQuoteTypes.wall) {
                    completedQuotes.push({
                        ...assignment,
                        _displayId: `${assignment._id}_wall`,
                        _originalId: assignment._id,
                        _quoteType: 'wall',
                        _quoteData: assignment.quotes.wall,
                        _isCombined: false
                    });
                }

                if (assignment.quotes?.floor?.isRequired &&
                    assignment.quotes?.floor?.designStatus === 'completed' &&
                    selectedQuoteTypes.floor) {
                    completedQuotes.push({
                        ...assignment,
                        _displayId: `${assignment._id}_floor`,
                        _originalId: assignment._id,
                        _quoteType: 'floor',
                        _quoteData: assignment.quotes.floor,
                        _isCombined: false
                    });
                }

                if (completedQuotes.length > 0) {
                    expanded.push(...completedQuotes);
                }
            }
        });

        return expanded;
    };

    const useInfiniteScroll = (callback, hasMore, isLoading) => {
        const [isFetching, setIsFetching] = useState(false);

        useEffect(() => {
            if (!isFetching) return;
            fetchMoreData();
        }, [isFetching]);

        const fetchMoreData = async () => {
            await callback();
            setIsFetching(false);
        };

        useEffect(() => {
            const handleScroll = () => {
                if (window.innerHeight + document.documentElement.scrollTop !== document.documentElement.offsetHeight || isFetching || !hasMore || isLoading) return;
                setIsFetching(true);
            };

            window.addEventListener('scroll', handleScroll);
            return () => window.removeEventListener('scroll', handleScroll);
        }, [isFetching, hasMore, isLoading]);

        return [isFetching, setIsFetching];
    };

    const [isFetching] = useInfiniteScroll(onLoadMore, hasMore, isLoadingMore);

    const STORAGE_KEYS = {
        columnVisibility: 'production_columnVisibility',
        columnSizing: 'production_columnSizing'
    };

    const saveToStorage = (key, value) => {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
            console.warn('Failed to save to localStorage:', error);
        }
    };

    const loadFromStorage = (key, defaultValue) => {
        try {
            const stored = localStorage.getItem(key);
            return stored ? JSON.parse(stored) : defaultValue;
        } catch (error) {
            console.warn('Failed to load from localStorage:', error);
            return defaultValue;
        }
    };

    const [columnVisibility, setColumnVisibility] = useState(() =>
        loadFromStorage(STORAGE_KEYS.columnVisibility, {
            uniqueId: true,
            customerName: true,
            jobType: true,
            mainStatus: true,
            quotes: true,
            assignee: true,
            priority: true,
            createdAt: true,
            invoiced: true,
            etaDate: true
        })
    );

    const [columnSizing, setColumnSizing] = useState(() =>
        loadFromStorage(STORAGE_KEYS.columnSizing, {})
    );

    // Detect navbar and scroll
    useEffect(() => {
        const detectNavbarHeight = () => {
            const navbar =
                document.querySelector('nav') ||
                document.querySelector('[role="navigation"]') ||
                document.querySelector('header');

            if (navbar) {
                const rect = navbar.getBoundingClientRect();
                setNavbarHeight(rect.height);
            } else {
                setNavbarHeight(0);
            }
        };

        const handleScroll = () => {
            if (columnToggleRef.current) {
                const rect = columnToggleRef.current.getBoundingClientRect();
                setShowStickyToolbar(rect.bottom < navbarHeight);
            }
        };

        detectNavbarHeight();
        window.addEventListener('resize', detectNavbarHeight);
        window.addEventListener('scroll', handleScroll);
        handleScroll();

        return () => {
            window.removeEventListener('resize', detectNavbarHeight);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [navbarHeight]);

    // Filter assignments based on search
    useEffect(() => {
        if (!debouncedSearchQuery.trim()) {
            setFilteredAssignments(assignments);
            return;
        }

        setIsTableLoading(true);

        // Client-side filtering for now
        const query = debouncedSearchQuery.toLowerCase();
        const filtered = assignments.filter(assignment => {
            return (
                assignment.uniqueId?.toLowerCase().includes(query) ||
                assignment._id?.toLowerCase().includes(query) ||
                assignment.customerName?.toLowerCase().includes(query) ||
                assignment.carpenterSupervisorName?.toLowerCase().includes(query) ||
                assignment.jobType?.toLowerCase().includes(query) ||
                assignment.mainStatus?.toLowerCase().includes(query) ||
                assignment.estimator?.name?.toLowerCase().includes(query) ||
                assignment.quotes?.roof?.designers?.some(d => d.name?.toLowerCase().includes(query)) ||
                assignment.quotes?.wall?.designers?.some(d => d.name?.toLowerCase().includes(query)) ||
                assignment.quotes?.floor?.designers?.some(d => d.name?.toLowerCase().includes(query))
            );
        });

        setFilteredAssignments(filtered);
        setIsTableLoading(false);
    }, [debouncedSearchQuery, assignments]);

    useEffect(() => {
        saveToStorage(STORAGE_KEYS.columnVisibility, columnVisibility);
    }, [columnVisibility]);

    useEffect(() => {
        saveToStorage(STORAGE_KEYS.columnSizing, columnSizing);
    }, [columnSizing]);

    useEffect(() => {
        const expanded = expandAssignmentsByQuoteType(filteredAssignments);
        setExpandedAssignments(expanded);
    }, [filteredAssignments, viewMode, selectedQuoteTypes]);


    const handleOpenAssignment = (assignmentId) => {
        window.open(`/${companySlug}/assignment/${assignmentId}`, '_blank');
    };

    const handleEditAssignment = (assignmentId) => {
        window.open(`/${companySlug}/assignment/${assignmentId}/edit`, '_blank');
    };

    const handleProduction = async (assignmentId, action, quoteType) => {
        try {
            await handleProductionAction(assignmentId, action, quoteType);
            // Optionally refresh data or show success message
            console.log(`Production ${action} completed successfully for ${quoteType}`);
        } catch (error) {
            console.error(`Failed to ${action} production:`, error);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-AU', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit'
        });
    };

    const getPriorityIndicator = (assignment) => {
        const isUrgent = assignment.jobInformation?.urgentQuote || assignment.jobInformation?.urgentDesign;
        if (isUrgent) return { color: 'bg-red-500', label: 'Urgent' };
        return { color: 'bg-gray-300', label: 'Normal' };
    };

    const getQuoteIcon = (quoteType) => {
        switch (quoteType) {
            case 'roof': return <FiHome className="w-3 h-3" />;
            case 'wall': return <BrickWall className="w-3 h-3" />;
            case 'floor': return <Grid2X2 className="w-3 h-3" />;
            default: return <FiLayers className="w-3 h-3" />;
        }
    };

    const getStatusDot = (status) => {
        switch (status) {
            case 'completed': return 'bg-green-500';
            case 'in_progress': return 'bg-yellow-500';
            case 'waiting_assignment': return 'bg-red-500';
            default: return 'bg-gray-400';
        }
    };

    const ProductionStatusTooltip = ({ assignment, quoteType }) => {
        const [isHovered, setIsHovered] = useState(false);
        const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, showAbove: false });
        const [triggerRef, setTriggerRef] = useState(null);

        // Calculate tooltip position with viewport awareness
        useEffect(() => {
            if (isHovered && triggerRef) {
                const rect = triggerRef.getBoundingClientRect();
                const tooltipHeight = quoteType ? 300 : 400; // Taller for combined view
                const viewportHeight = window.innerHeight;
                const spaceBelow = viewportHeight - rect.bottom;
                const spaceAbove = rect.top;

                const showAbove = spaceBelow < tooltipHeight && spaceAbove > tooltipHeight;

                setTooltipPosition({
                    x: rect.left,
                    y: showAbove ? rect.top - 8 : rect.bottom + 8,
                    showAbove
                });
            }
        }, [isHovered, triggerRef, quoteType]);

        // Smart status text for combined view
        const getSmartStatusText = () => {
            const requiredQuotes = getRequiredQuotes();
            const phases = requiredQuotes.map(t => ({
                type: t,
                phase: assignment.quotes?.[t]?.production?.phase || 'not_started' // CHANGED
            }));

            const counts = {
                completed: phases.filter(p => p.phase === 'completed').length,
                in_production: phases.filter(p => p.phase === 'in_production').length,
                in_cutting: phases.filter(p => p.phase === 'in_cutting').length,
                cutting_ended: phases.filter(p => p.phase === 'cutting_ended').length,
                not_started: phases.filter(p => p.phase === 'not_started').length
            };

            const total = requiredQuotes.length;

            // Helper to get color for each state
            const getStateColor = (state) => {
                switch (state) {
                    case 'completed': return { text: 'text-green-600', bg: 'bg-green-500' };
                    case 'in_production': return { text: 'text-blue-600', bg: 'bg-blue-500' };
                    case 'in_cutting': return { text: 'text-yellow-600', bg: 'bg-yellow-500' };
                    case 'cutting_ended': return { text: 'text-purple-600', bg: 'bg-purple-500' };
                    case 'not_started': return { text: 'text-gray-500', bg: 'bg-gray-400' };
                    default: return { text: 'text-gray-600', bg: 'bg-gray-400' };
                }
            };

            // Get dots array (one dot per quote with its phase color)
            const dots = phases.map(p => getStateColor(p.phase).bg);

            // All same state checks
            if (counts.completed === total) {
                return {
                    parts: [{ text: 'All Complete', color: 'text-green-600' }],
                    dots,
                    primaryDot: 'bg-green-500'
                };
            }

            if (counts.in_cutting === total) {
                return {
                    parts: [{ text: 'All Cutting', color: 'text-yellow-600' }],
                    dots,
                    primaryDot: 'bg-yellow-500'
                };
            }

            if (counts.cutting_ended === total) {
                return {
                    parts: [{ text: 'All Ready', color: 'text-purple-600' }],
                    dots,
                    primaryDot: 'bg-purple-500'
                };
            }

            if (counts.in_production === total) {
                return {
                    parts: [{ text: 'All Producing', color: 'text-blue-600' }],
                    dots,
                    primaryDot: 'bg-blue-500'
                };
            }

            if (counts.not_started === total) {
                return {
                    parts: [{ text: 'Not Started', color: 'text-gray-500' }],
                    dots,
                    primaryDot: 'bg-gray-400'
                };
            }

            // Build colored status parts for mixed states
            const statusParts = [];

            // Add cutting (yellow)
            if (counts.in_cutting > 0) {
                statusParts.push({
                    text: `${counts.in_cutting}C`,
                    color: 'text-yellow-600',
                    priority: 1
                });
            }

            // Add producing (blue)
            if (counts.in_production > 0) {
                statusParts.push({
                    text: `${counts.in_production}P`,
                    color: 'text-blue-600',
                    priority: 2
                });
            }

            // Add ready (purple)
            if (counts.cutting_ended > 0) {
                statusParts.push({
                    text: `${counts.cutting_ended}R`,
                    color: 'text-purple-600',
                    priority: 3
                });
            }

            // Add completed (green)
            if (counts.completed > 0) {
                statusParts.push({
                    text: `${counts.completed}D`,
                    color: 'text-green-600',
                    priority: 4
                });
            }

            // Add not started (gray) - only if there's active work
            if (counts.not_started > 0 && (counts.in_cutting > 0 || counts.in_production > 0 || counts.cutting_ended > 0)) {
                statusParts.push({
                    text: `${counts.not_started}NS`,
                    color: 'text-gray-500',
                    priority: 5
                });
            }

            // Determine primary dot color based on most significant active state
            let primaryDot = 'bg-gray-400';
            if (counts.in_cutting > 0) {
                primaryDot = 'bg-yellow-500';
            } else if (counts.in_production > 0) {
                primaryDot = 'bg-blue-500';
            } else if (counts.cutting_ended > 0) {
                primaryDot = 'bg-purple-500';
            } else if (counts.completed > 0) {
                primaryDot = 'bg-green-500';
            }

            // Sort by priority
            const sortedParts = statusParts.sort((a, b) => a.priority - b.priority);

            return {
                parts: sortedParts,
                dots,
                primaryDot
            };
        };

        const getProductionStatus = () => {
            // If separated view, get specific quote type production
            if (quoteType) {
                const production = assignment.quotes?.[quoteType]?.production; // CHANGED
                if (!production?.phase) return 'not_started';
                return production.phase;
            }

            // Combined view - use smart status
            return getSmartStatusText();
        };

        const getProductionStatusColor = () => {
            if (quoteType) {
                // Separated view - use phase color
                const phase = assignment.quotes?.[quoteType]?.production?.phase || 'not_started'; // CHANGED
                switch (phase) {
                    case 'completed': return 'text-green-500';
                    case 'in_production': return 'text-blue-500';
                    case 'cutting_ended': return 'text-purple-500';
                    case 'in_cutting': return 'text-yellow-500';
                    case 'not_started': return 'text-gray-500';
                    default: return 'text-gray-400';
                }
            }

            // Combined view - use smart status color
            return getSmartStatusText().color;
        };

        const getProductionStatusText = () => {
            if (quoteType) {
                // Separated view - use phase text
                const phase = assignment.quotes?.[quoteType]?.production?.phase || 'not_started'; // CHANGED
                switch (phase) {
                    case 'completed': return 'Completed';
                    case 'in_production': return 'In Production';
                    case 'cutting_ended': return 'Cutting Ended';
                    case 'in_cutting': return 'In Cutting';
                    case 'not_started': return 'Not Started';
                    default: return 'Unknown';
                }
            }

            // Combined view - use smart status text
            return getSmartStatusText().text;
        };

        const getStatusDot = (phase) => {
            // If phase is an object (from getSmartStatusText), return its dot property
            if (typeof phase === 'object' && phase.dot) {
                return phase.dot;
            }

            // Otherwise treat as string phase
            switch (phase) {
                case 'completed': return 'bg-green-500';
                case 'in_production': return 'bg-blue-500';
                case 'cutting_ended': return 'bg-purple-500';
                case 'in_cutting': return 'bg-yellow-500';
                case 'not_started': return 'bg-gray-400';
                default: return 'bg-gray-400';
            }
        };

        const formatDate = (dateString) => {
            if (!dateString) return 'N/A';
            return new Date(dateString).toLocaleString('en-AU', {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        };

        const calculateDuration = (startDate, endDate) => {
            if (!startDate) return 'N/A';

            const start = new Date(startDate);
            const end = endDate ? new Date(endDate) : new Date();
            const diffMs = end - start;

            const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

            if (days > 0) {
                return `${days}d ${hours}h`;
            } else if (hours > 0) {
                return `${hours}h ${minutes}m`;
            } else {
                return `${minutes}m`;
            }
        };

        // Production progress steps
        const progressSteps = [
            { key: 'not_started', label: 'Not Started', color: 'gray' },
            { key: 'in_cutting', label: 'Cutting', color: 'yellow' },
            { key: 'cutting_ended', label: 'Cut Done', color: 'purple' },
            { key: 'in_production', label: 'Production', color: 'blue' },
            { key: 'completed', label: 'Completed', color: 'green' }
        ];

        const getCurrentStepIndex = (phase) => {
            return progressSteps.findIndex(step => step.key === phase);
        };

        const getStepColor = (stepKey, currentPhase) => {
            const currentIndex = getCurrentStepIndex(currentPhase);
            const stepIndex = progressSteps.findIndex(step => step.key === stepKey);

            if (stepIndex < currentIndex) return 'bg-green-500';
            if (stepIndex === currentIndex) {
                switch (stepKey) {
                    case 'in_cutting': return 'bg-yellow-500';
                    case 'cutting_ended': return 'bg-purple-500';
                    case 'in_production': return 'bg-blue-500';
                    case 'completed': return 'bg-green-500';
                    default: return 'bg-gray-300';
                }
            }
            return 'bg-gray-300';
        };

        const getStepTextColor = (stepKey, currentPhase) => {
            const currentIndex = getCurrentStepIndex(currentPhase);
            const stepIndex = progressSteps.findIndex(step => step.key === stepKey);

            if (stepIndex <= currentIndex) return 'text-gray-900 font-semibold';
            return 'text-gray-400';
        };

        const getQuoteIcon = (type) => {
            switch (type) {
                case 'roof': return <FiHome className="w-3 h-3" />;
                case 'wall': return <BrickWall className="w-3 h-3" />;
                case 'floor': return <Grid2X2 className="w-3 h-3" />;
                default: return <FiLayers className="w-3 h-3" />;
            }
        };

        // Get required quotes for combined view
        const getRequiredQuotes = () => {
            return ['roof', 'wall', 'floor'].filter(type =>
                assignment.quotes?.[type]?.isRequired
            );
        };

        // Compact tooltip content for SEPARATED view
        const SeparatedTooltipContent = () => {
            const currentStatus = getProductionStatus();
            const currentIndex = getCurrentStepIndex(currentStatus);
            const production = assignment.quotes?.[quoteType]?.production;

            return (
                <div
                    className="fixed z-[9999] w-80 bg-white border border-gray-200 rounded-lg shadow-xl p-3 pointer-events-none"
                    style={{
                        left: `${tooltipPosition.x}px`,
                        top: tooltipPosition.showAbove ? `${tooltipPosition.y}px` : `${tooltipPosition.y}px`,
                        transform: `${tooltipPosition.x > window.innerWidth - 320 ? 'translateX(-100%)' : 'translateX(0)'} ${tooltipPosition.showAbove ? 'translateY(-100%)' : 'translateY(0)'}`
                    }}
                >
                    <div
                        className={`absolute w-3 h-3 bg-white border border-gray-200 transform rotate-45 ${tooltipPosition.showAbove ? 'border-t-0 border-l-0' : 'border-b-0 border-r-0'}`}
                        style={{
                            [tooltipPosition.showAbove ? 'bottom' : 'top']: '-6px',
                            left: tooltipPosition.x > window.innerWidth - 320 ? 'calc(100% - 20px)' : '12px'
                        }}
                    ></div>

                    <div className="space-y-3">
                        <h4 className="font-medium text-gray-900 text-sm capitalize">{quoteType} Production Status</h4>

                        <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-700">Current Phase:</span>
                            <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${getStatusDot(currentStatus)}`}></div>
                                <span className={`text-xs font-medium ${getProductionStatusColor()}`}>
                                    {getProductionStatusText()}
                                </span>
                            </div>
                        </div>

                        {currentStatus !== 'not_started' && (
                            <div className="border-t border-gray-100 pt-3">
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between relative">
                                        {progressSteps.slice(1).map((step, idx) => {
                                            const stepIndex = idx + 1;
                                            const isCompleted = currentIndex > stepIndex || (step.key === 'completed' && currentStatus === 'completed');
                                            const isCurrentAndNotCompleted = currentIndex === stepIndex && !isCompleted;

                                            return (
                                                <div key={step.key} className="flex flex-col items-center flex-1 relative">
                                                    {idx < progressSteps.length - 2 && (
                                                        <div className="absolute top-3 left-1/2 w-full h-0.5 -z-10">
                                                            <div className={`h-full ${currentIndex > stepIndex ? 'bg-green-500' : 'bg-gray-300'}`} />
                                                        </div>
                                                    )}

                                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 border-white shadow-sm ${getStepColor(step.key, currentStatus)} ${isCurrentAndNotCompleted ? 'animate-pulse' : ''}`}>
                                                        {isCompleted && <FiCheckCircle className="w-3 h-3 text-white" />}
                                                    </div>

                                                    <span className={`text-[10px] mt-1 text-center ${getStepTextColor(step.key, currentStatus)}`}>
                                                        {step.label}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}

                        {production && production.userId ? (
                            <>
                                <div className="border-t border-gray-100 pt-2">
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="font-medium text-gray-700">Lead:</span>
                                        <span className="text-gray-600">{production.name}</span>
                                    </div>
                                </div>

                                <div className="space-y-1 text-xs">
                                    {production.startCuttingAt && (
                                        <>
                                            <div className="flex items-center justify-between">
                                                <span className="font-medium text-gray-700">Cutting Started:</span>
                                                <span className="text-gray-600">{formatDate(production.startCuttingAt)}</span>
                                            </div>
                                            {production.endCuttingAt && (
                                                <>
                                                    <div className="flex items-center justify-between">
                                                        <span className="font-medium text-gray-700">Cutting Ended:</span>
                                                        <span className="text-gray-600">{formatDate(production.endCuttingAt)}</span>
                                                    </div>
                                                    <div className="flex items-center justify-between">
                                                        <span className="font-medium text-gray-700">Cutting Duration:</span>
                                                        <span className="text-blue-600 font-medium">
                                                            {calculateDuration(production.startCuttingAt, production.endCuttingAt)}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                            {currentStatus === 'in_cutting' && (
                                                <div className="flex items-center justify-between">
                                                    <span className="font-medium text-gray-700">Cutting Time:</span>
                                                    <span className="text-yellow-600 font-medium">
                                                        {calculateDuration(production.startCuttingAt, null)}
                                                    </span>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {production.startProductionAt && (
                                        <>
                                            <div className="flex items-center justify-between">
                                                <span className="font-medium text-gray-700">Production Started:</span>
                                                <span className="text-gray-600">{formatDate(production.startProductionAt)}</span>
                                            </div>
                                            {production.endProductionAt && (
                                                <>
                                                    <div className="flex items-center justify-between">
                                                        <span className="font-medium text-gray-700">Production Ended:</span>
                                                        <span className="text-gray-600">{formatDate(production.endProductionAt)}</span>
                                                    </div>
                                                    <div className="flex items-center justify-between">
                                                        <span className="font-medium text-gray-700">Production Duration:</span>
                                                        <span className="text-blue-600 font-medium">
                                                            {calculateDuration(production.startProductionAt, production.endProductionAt)}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                            {currentStatus === 'in_production' && (
                                                <div className="flex items-center justify-between">
                                                    <span className="font-medium text-gray-700">Production Time:</span>
                                                    <span className="text-blue-600 font-medium">
                                                        {calculateDuration(production.startProductionAt, null)}
                                                    </span>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {currentStatus === 'completed' && production.startCuttingAt && production.endProductionAt && (
                                        <div className="flex items-center justify-between border-t pt-1 mt-1">
                                            <span className="font-semibold text-gray-800">Total Duration:</span>
                                            <span className="text-green-600 font-bold">
                                                {calculateDuration(production.startCuttingAt, production.endProductionAt)}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {(currentStatus === 'in_cutting' || currentStatus === 'in_production') && (
                                    <div className="border-t border-gray-100 pt-2">
                                        <div className="flex items-center justify-center gap-2 text-xs">
                                            <div className={`w-2 h-2 rounded-full animate-pulse ${currentStatus === 'in_cutting' ? 'bg-yellow-500' : 'bg-blue-500'}`}></div>
                                            <span className={`font-medium ${currentStatus === 'in_cutting' ? 'text-yellow-600' : 'text-blue-600'}`}>
                                                {currentStatus === 'in_cutting' ? 'Currently Cutting' : 'Currently in Production'}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {currentStatus === 'cutting_ended' && (
                                    <div className="border-t border-gray-100 pt-2">
                                        <div className="flex items-center justify-center gap-2 text-xs">
                                            <FiClock className="w-3 h-3 text-purple-500" />
                                            <span className="text-purple-600 font-medium">Ready to start production</span>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="border-t border-gray-100 pt-2 text-center">
                                <p className="text-xs text-gray-500 italic">Production not started yet</p>
                            </div>
                        )}
                    </div>
                </div>
            );
        };

        // Combined tooltip content - shows all quote types
        const CombinedTooltipContent = () => {
            const requiredQuotes = getRequiredQuotes();

            return (
                <div
                    className="fixed z-[9999] w-96 bg-white border border-gray-200 rounded-lg shadow-xl p-3 pointer-events-none"
                    style={{
                        left: `${tooltipPosition.x}px`,
                        top: tooltipPosition.showAbove ? `${tooltipPosition.y}px` : `${tooltipPosition.y}px`,
                        transform: `${tooltipPosition.x > window.innerWidth - 384 ? 'translateX(-100%)' : 'translateX(0)'} ${tooltipPosition.showAbove ? 'translateY(-100%)' : 'translateY(0)'}`
                    }}
                >
                    <div
                        className={`absolute w-3 h-3 bg-white border border-gray-200 transform rotate-45 ${tooltipPosition.showAbove ? 'border-t-0 border-l-0' : 'border-b-0 border-r-0'}`}
                        style={{
                            [tooltipPosition.showAbove ? 'bottom' : 'top']: '-6px',
                            left: tooltipPosition.x > window.innerWidth - 384 ? 'calc(100% - 20px)' : '12px'
                        }}
                    ></div>

                    <div className="space-y-3">
                        <h4 className="font-medium text-gray-900 text-sm">Production Status by Quote Type</h4>

                        {/* Overall Status */}
                        <div className="flex items-center justify-between bg-gray-50 rounded-md p-2">
                            <span className="text-xs font-medium text-gray-700">Overall Status:</span>
                            <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${getStatusDot(getProductionStatus())}`}></div>
                                <span className={`text-xs font-medium ${getProductionStatusColor()}`}>
                                    {getProductionStatusText()}
                                </span>
                            </div>
                        </div>

                        {/* Per Quote Type Status */}
                        <div className="space-y-2">
                            {requiredQuotes.map(type => {
                                const production = assignment.quotes?.[type]?.production; // CHANGED
                                const phase = production?.phase || 'not_started';
                                const phaseLabel = phase.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

                                return (
                                    <div key={type} className="border border-gray-200 rounded-lg p-2">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                {getQuoteIcon(type)}
                                                <span className="text-xs font-semibold text-gray-900 capitalize">{type}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className={`w-2 h-2 rounded-full ${getStatusDot(phase)}`}></div>
                                                <span className={`text-xs font-medium ${phase === 'completed' ? 'text-green-600' :
                                                    phase === 'in_production' ? 'text-blue-600' :
                                                        phase === 'cutting_ended' ? 'text-purple-600' :
                                                            phase === 'in_cutting' ? 'text-yellow-600' :
                                                                'text-gray-500'
                                                    }`}>
                                                    {phaseLabel}
                                                </span>
                                            </div>
                                        </div>

                                        {production?.userId && (
                                            <div className="text-xs space-y-1">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-gray-600">Lead:</span>
                                                    <span className="text-gray-900 font-medium">{production.name}</span>
                                                </div>

                                                {production.startCuttingAt && (
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-gray-600">
                                                            {phase === 'in_cutting' ? 'Cutting:' : 'Cut Time:'}
                                                        </span>
                                                        <span className="text-gray-900 font-medium">
                                                            {calculateDuration(production.startCuttingAt, production.endCuttingAt || null)}
                                                        </span>
                                                    </div>
                                                )}

                                                {production.startProductionAt && (
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-gray-600">
                                                            {phase === 'in_production' ? 'Producing:' : 'Prod Time:'}
                                                        </span>
                                                        <span className="text-gray-900 font-medium">
                                                            {calculateDuration(production.startProductionAt, production.endProductionAt || null)}
                                                        </span>
                                                    </div>
                                                )}

                                                {phase === 'completed' && production.startCuttingAt && production.endProductionAt && (
                                                    <div className="flex items-center justify-between border-t pt-1 mt-1">
                                                        <span className="text-gray-800 font-semibold">Total:</span>
                                                        <span className="text-green-600 font-bold">
                                                            {calculateDuration(production.startCuttingAt, production.endProductionAt)}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {!production?.userId && (
                                            <p className="text-xs text-gray-500 italic text-center">Not started</p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Status Code Legend */}
                        <div className="border-t border-gray-100 pt-2 mt-2">
                            <p className="text-xs font-medium text-gray-700 mb-1">Status Codes:</p>
                            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-600">
                                <div className="flex items-center gap-1">
                                    <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                                    <span>C = Cutting</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                                    <span>P = Producing</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                                    <span>R = Ready</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                    <span>D = Done</span>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            );
        };

        return (
            <>
                <div
                    ref={setTriggerRef}
                    className="inline-flex items-center gap-2 px-2 py-1 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors cursor-help"
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {/* Multiple dots for combined view, single dot for separated */}
                    {quoteType ? (
                        // Separated view - single dot
                        <div className={`w-2 h-2 rounded-full ${getStatusDot(assignment.quotes?.[quoteType]?.production?.phase || 'not_started')}`}></div> // CHANGED
                    ) : (
                        // Combined view - multiple dots (one per quote)
                        <div className="flex items-center gap-0.5">
                            {getSmartStatusText().dots.map((dotColor, idx) => (
                                <div key={idx} className={`w-2 h-2 rounded-full ${dotColor}`}></div>
                            ))}
                        </div>
                    )}

                    {/* Status text with colored parts */}
                    {quoteType ? (
                        // Separated view - single color
                        <span className={`text-xs font-medium ${getProductionStatusColor()}`}>
                            {getProductionStatusText()}
                        </span>
                    ) : (
                        // Combined view - multi-colored text
                        <span className="text-xs font-medium flex items-center gap-1">
                            {getSmartStatusText().parts.map((part, idx) => (
                                <React.Fragment key={idx}>
                                    {idx > 0 && <span className="text-gray-400">+</span>}
                                    <span className={part.color}>{part.text}</span>
                                </React.Fragment>
                            ))}
                        </span>
                    )}
                </div>

                {isHovered && createPortal(
                    quoteType ? <SeparatedTooltipContent /> : <CombinedTooltipContent />,
                    document.body
                )}
            </>
        );
    };

    // Table columns definition
    const columns = [
        {
            id: 'uniqueId',
            accessorKey: 'uniqueId',
            header: () => (
                <div className="flex items-center">
                    <FiHash className="w-3 h-3 mr-2 text-gray-500" />
                    Job ID
                </div>
            ),
            cell: ({ row }) => (
                <div className="min-w-0 flex-1">
                    <div className="font-semibold text-gray-900 truncate group-hover:text-blue-700 transition-colors">
                        {row.original.uniqueId}
                        {row.original._quoteType && (
                            <span className="ml-2 text-xs font-medium text-gray-500">
                                ({row.original._quoteType.charAt(0).toUpperCase() + row.original._quoteType.slice(1)})
                            </span>
                        )}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                        ID: {row.original._id.slice(-9)}
                    </div>
                </div>
            ),
            size: 180,
            minSize: 120,
            maxSize: 300,
        },
        {
            id: 'customerName',
            accessorKey: 'customerName',
            header: () => (
                <div className="flex items-center">
                    <FiUser className="w-3 h-3 mr-2 text-gray-500" />
                    Customer
                </div>
            ),
            cell: ({ row }) => (
                <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-800 truncate">
                        {row.original.customerName}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 truncate">
                        {row.original.carpenterSupervisorName || 'No supervisor'}
                    </div>
                </div>
            ),
            size: 200,
            minSize: 150,
            maxSize: 300,
        },
        {
            id: 'jobType',
            accessorKey: 'jobType',
            header: () => (
                <div className="flex items-center justify-center">
                    <FiTool className="w-3 h-3 mr-1 text-gray-500" />
                    Type
                </div>
            ),
            cell: ({ getValue }) => (
                <div className="text-center">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium shadow-sm ${getValue() === 'Design' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                        getValue() === 'Estimation' ? 'bg-purple-100 text-purple-800 border border-purple-200' :
                            'bg-emerald-100 text-emerald-800 border border-emerald-200'
                        }`}>
                        {getValue() === 'Design' ? 'D' : getValue() === 'Estimation' ? 'E' : 'D&E'}
                    </span>
                </div>
            ),
            size: 100,
            minSize: 80,
            maxSize: 120,
        },
        {
            id: 'mainStatus',
            accessorKey: 'mainStatus',
            header: () => (
                <div className="flex items-center justify-center">
                    <FiClock className="w-3 h-3 mr-1 text-gray-500" />
                    Production Status
                </div>
            ),
            cell: ({ row }) => (
                <div className="flex items-center justify-center">
                    <span className="text-xs text-gray-600">
                        <ProductionStatusTooltip
                            assignment={row.original}
                            quoteType={row.original._quoteType} // Pass the quote type for separated view
                        />
                    </span>
                </div>
            ),
            size: 140,
            minSize: 100,
            maxSize: 160,
        },
        {
            id: 'quotes',
            header: () => (
                <div className="flex items-center justify-center">
                    <FiLayers className="w-3 h-3 mr-1 text-gray-500" />
                    Quotes
                </div>
            ),
            cell: ({ row }) => (
                <div className="flex items-center justify-center gap-1">
                    {row.original.quotes?.roof?.isRequired && (
                        <FiHome className="w-4 h-4 text-blue-500" />
                    )}
                    {row.original.quotes?.wall?.isRequired && (
                        <BrickWall className="w-4 h-4 text-purple-500" />
                    )}
                    {row.original.quotes?.floor?.isRequired && (
                        <Grid2X2 className="w-4 h-4 text-emerald-500" />
                    )}
                </div>
            ),
            size: 160,
            minSize: 120,
            maxSize: 180,
        },
        {
            id: 'assignee',
            header: () => (
                <div className="flex items-center">
                    <FiUser className="w-3 h-3 mr-2 text-gray-500" />
                    Assignee
                </div>
            ),
            cell: ({ row }) => (
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-700 truncate">
                        {row.original.estimator?.name ||
                            row.original.quotes?.roof?.designers?.[0]?.name ||
                            row.original.quotes?.wall?.designers?.[0]?.name ||
                            row.original.quotes?.floor?.designers?.[0]?.name ||
                            'Unassigned'}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                        {row.original.estimator?.name ? 'Estimator' :
                            (row.original.quotes?.roof?.designers?.[0] || row.original.quotes?.wall?.designers?.[0] || row.original.quotes?.floor?.designers?.[0]) ? 'Designer' :
                                'No assignment'}
                    </div>
                </div>
            ),
            size: 160,
            minSize: 120,
            maxSize: 200,
        },
        {
            id: 'priority',
            header: () => (
                <div className="flex items-center justify-center">
                    <FiAlertCircle className="w-3 h-3 mr-1 text-gray-500" />
                    Priority
                </div>
            ),
            cell: ({ row }) => {
                const priority = getPriorityIndicator(row.original);
                const urgentQuote = row.original.jobInformation?.urgentQuote;
                const urgentDesign = row.original.jobInformation?.urgentDesign;
                return (
                    <div className="flex items-center justify-center gap-1">
                        <div className={`w-3 h-3 rounded-full ${priority.color} shadow-sm`} title={priority.label}></div>
                        <div className="flex gap-1">
                            {urgentQuote && (
                                <span className="px-1 py-0.5 text-xs bg-red-100 text-red-700 rounded border" title="Urgent Quote">
                                    UQ
                                </span>
                            )}
                            {urgentDesign && (
                                <span className="px-1 py-0.5 text-xs bg-orange-100 text-orange-700 rounded border" title="Urgent Design">
                                    UD
                                </span>
                            )}
                        </div>
                    </div>
                );
            },
            size: 120,
            minSize: 100,
            maxSize: 140,
        },
        {
            id: 'invoiced',
            accessorKey: 'invoiced',
            header: () => (
                <div className="flex items-center justify-center">
                    <FiDollarSign className="w-3 h-3 mr-1 text-gray-500" />
                    Invoice
                </div>
            ),
            cell: ({ getValue }) => (
                <div className="flex justify-center">
                    {getValue() ? (
                        <div className="flex items-center gap-1">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="text-xs font-medium text-green-700">Paid</span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-1">
                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                            <span className="text-xs font-medium text-gray-600">Pending</span>
                        </div>
                    )}
                </div>
            ),
            size: 90,
            minSize: 70,
            maxSize: 100,
        },
        {
            id: 'createdAt',
            accessorKey: 'createdAt',
            header: () => (
                <div className="flex items-center justify-center">
                    <FiCalendar className="w-3 h-3 mr-1 text-gray-500" />
                    Created
                </div>
            ),
            cell: ({ getValue }) => {
                const date = new Date(getValue());
                return (
                    <div className="text-center">
                        <div className="text-sm font-medium text-gray-700">
                            {date.toLocaleDateString('en-AU')}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                            {date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                    </div>
                );
            },
            size: 110,
            minSize: 90,
            maxSize: 130,
        },
        {
            id: 'etaDate',
            header: () => (
                <div className="flex items-center justify-center">
                    <FiCalendar className="w-3 h-3 mr-1 text-gray-500" />
                    ETA Date
                </div>
            ),
            cell: ({ row }) => {
                const quotes = row.original.quotes || {};
                const etaDates = [
                    quotes.roof?.expectedFinishDate,
                    quotes.wall?.expectedFinishDate,
                    quotes.floor?.expectedFinishDate,
                    row.original.expectedEstimationFinishDate,
                    row.original.bookingDeliveryDate
                ].filter(Boolean);

                const latestDate = etaDates.length > 0 ?
                    new Date(Math.max(...etaDates.map(date => new Date(date)))) : null;

                return (
                    <div className="text-center">
                        <div className="text-sm font-medium text-gray-700">
                            {formatDate(latestDate)}
                        </div>
                        {latestDate && (
                            <div className="text-xs text-gray-500 mt-0.5">
                                {Math.ceil((new Date(latestDate) - new Date()) / (1000 * 60 * 60 * 24))} days
                            </div>
                        )}
                    </div>
                );
            },
            size: 120,
            minSize: 100,
            maxSize: 140,
        },
    ];

    const table = useReactTable({
        data: expandedAssignments,
        columns,
        getCoreRowModel: getCoreRowModel(),
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        onColumnVisibilityChange: setColumnVisibility,
        onColumnSizingChange: setColumnSizing,
        defaultColumn: {
            minSize: 60,
            maxSize: 600,
        },
        state: {
            columnVisibility,
            columnSizing,
        },
    });

    const LoadingRow = () => (
        <tr>
            <td colSpan={columns.length} className="px-4 py-8 text-center">
                <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="text-gray-600">Loading more assignments...</span>
                </div>
            </td>
        </tr>
    );

    return (
        <div className="">
            <StickyToolbar
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                selectedQuoteTypes={selectedQuoteTypes}
                setSelectedQuoteTypes={setSelectedQuoteTypes}
                viewMode={viewMode}
                setViewMode={setViewMode}
                columnVisibility={columnVisibility}
                setColumnVisibility={setColumnVisibility}
                isVisible={showStickyToolbar}
                navbarHeight={navbarHeight}
                expandedAssignments={expandedAssignments}
                filteredAssignments={filteredAssignments}
            />

            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Production Ready Assignments</h3>
                <div className="flex items-center gap-2">
                    <span className="bg-orange-100 text-orange-800 text-sm font-medium px-3 py-1 rounded-full">
                        {searchQuery
                            ? `${expandedAssignments.length} rows (${filteredAssignments.length} assignments)`
                            : viewMode === 'separated'
                                ? `${expandedAssignments.length} rows (${assignments.length} assignments)`
                                : `${assignments.length} assignments`
                        }
                    </span>

                    <div className="flex items-center gap-2 ml-4">
                        <div className="flex bg-gray-200 rounded-md p-0.5">
                            <button
                                onClick={() => setViewMode('separated')}
                                className={`px-3 py-1 text-xs font-medium rounded transition-colors ${viewMode === 'separated'
                                    ? 'bg-white text-blue-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                Separated
                            </button>
                            <button
                                onClick={() => setViewMode('combined')}
                                className={`px-3 py-1 text-xs font-medium rounded transition-colors ${viewMode === 'combined'
                                    ? 'bg-white text-blue-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                Combined
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div ref={columnToggleRef}>
                <ColumnToggle
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    columnVisibility={columnVisibility}
                    setColumnVisibility={setColumnVisibility}
                    setColumnSizing={setColumnSizing}
                    selectedQuoteTypes={selectedQuoteTypes}
                    setSelectedQuoteTypes={setSelectedQuoteTypes}
                />
            </div>

            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                <table className="min-w-full table-fixed" style={{ width: table.getTotalSize() }}>
                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <th
                                        key={header.id}
                                        className="px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide border-r border-gray-200/70 last:border-r-0 relative"
                                        style={{ width: header.getSize() }}
                                    >
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                        <div
                                            onMouseDown={header.getResizeHandler()}
                                            onTouchStart={header.getResizeHandler()}
                                            className={`absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none bg-transparent hover:bg-blue-400 ${header.column.getIsResizing() ? 'bg-blue-500' : ''
                                                }`}
                                        />
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {(isTableLoading || isLoading) ? (
                            <tr>
                                <td colSpan={columns.length} className="px-4 py-16 text-center">
                                    <div className="flex flex-col items-center justify-center gap-3">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                        <span className="text-gray-600 font-medium">Loading assignments...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : filteredAssignments.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="px-4 py-12 text-center">
                                    <FiGrid className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                                    <p className="text-gray-500 font-medium">No assignments found</p>
                                </td>
                            </tr>
                        ) : (
                            <>
                                {table.getRowModel().rows.map((row, index) => {
                                    const isUrgent = row.original.jobInformation?.urgentQuote || row.original.jobInformation?.urgentDesign;
                                    return (
                                        <AssignmentContextMenu
                                            key={row.id}
                                            assignment={row.original}
                                            onOpenAssignment={handleOpenAssignment}
                                            onEditAssignment={handleEditAssignment}
                                            // onAssignDesigner={handleAssignDesigner}
                                            // availableDesigners={availableDesigners}
                                            page="production"
                                            onStartProduction={handleProduction}
                                            onEndProduction={handleProduction}
                                        >
                                            <tr
                                                key={row.id}
                                                className={`border-b border-gray-100/80 hover:bg-blue-50/50 cursor-pointer transition-all duration-150 text-sm group ${index % 2 === 0 ? 'bg-white' : 'bg-gray-100/50'
                                                    } ${isUrgent ? '!bg-red-50/80 !hover:bg-red-100/60 !border-red-100' : ''}`}
                                                onClick={() => navigate(`/${company}/assignment/${row.original._originalId || row.original._id}`)}
                                            >
                                                {row.getVisibleCells().map(cell => (
                                                    <td
                                                        key={cell.id}
                                                        className="px-4 py-2 border-r border-gray-200/40 last:border-r-0"
                                                        style={{ width: cell.column.getSize() }}
                                                    >
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                ))}
                                            </tr>
                                        </AssignmentContextMenu>
                                    );
                                })}
                                {isLoadingMore && <LoadingRow />}
                                {!hasMore && filteredAssignments.length > 0 && !searchQuery && (
                                    <tr>
                                        <td colSpan={columns.length} className="px-4 py-4 text-center text-gray-500 text-sm">
                                            All assignments loaded
                                        </td>
                                    </tr>
                                )}
                            </>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ProductionReadyTable;